// @ts-strict-ignore
import schema from './schema.json';
import { WorkflowEngineExceptionType } from './constants';
import type {
  SchemaObjectTypeValue,
  SchemaObjectProperty,
  SchemaArrayItems,
  SchemaInstance,
} from './types';

/* Alloy Workflow Editor Engine
 * Aspen v0.1
 * Todd Stonefield-Oh, Scott Clark, David Azar
 */

export class WorkflowEngineException extends Error {
  constructor(path: string, message: string) {
    super(`${path} / ${message}`);

    Object.setPrototypeOf(this, new.target.prototype);
    this.name = this.constructor.name;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

function initializeValueBasedOnTypeOfKey({
  object,
}: {
  object: SchemaObjectTypeValue;
}) {
  let initializedTemplateValue;
  switch (object?.type) {
    case 'string':
      initializedTemplateValue = '';
      break;
    case 'integer':
      initializedTemplateValue = 0;
      break;
    case 'object':
      initializedTemplateValue = object.properties
        ? generateObjectProperties({
          schemaObject: object.properties as SchemaObjectProperty,
        })
        : {};
      break;
    case 'array':
      initializedTemplateValue = generateArray({
        schemaObject: object.items as SchemaArrayItems,
      });
      break;
    default:
      initializedTemplateValue = '';
      break;
  }

  return initializedTemplateValue;
}

function generateArray({
  schemaObject,
}: {
  schemaObject: SchemaArrayItems;
}) {
  const generatedArray = [];

  if (Array.isArray(schemaObject)) {
    schemaObject.forEach(object => {
      const initializedTemplateValue = initializeValueBasedOnTypeOfKey({
        object,
      });

      generatedArray.push(initializedTemplateValue);
    });
  }

  return generatedArray;
}

function generateObjectProperties({
  schemaObject,
}: {
  schemaObject: SchemaObjectProperty;
}) {
  const generatedObject = {};

  Object.entries(schemaObject).forEach(([key, value]) => {
    const initializedTemplateValue = initializeValueBasedOnTypeOfKey({
      object: value,
    });

    const keyName = key === 'additionalProperties'
      ? ''
      : key;
    generatedObject[keyName] = initializedTemplateValue;
  });

  return generatedObject;
}

export default function initializeSchema() {
  if (!schema) {
    throw new WorkflowEngineException('', WorkflowEngineExceptionType.ERR_INITIALIZESCHEMA_NO_SCHEMA);
  }

  try {
    const instance = {};

    Object.entries(schema.properties).forEach(([key, value]) => {
      const initializedInstanceValue = initializeValueBasedOnTypeOfKey({
        object: value as SchemaObjectTypeValue,
      });

      instance[key] = initializedInstanceValue;
    });

    return instance as SchemaInstance;
  } catch (e) {
    throw new WorkflowEngineException('', WorkflowEngineExceptionType.ERR_INITIALIZESCHEMA_FAILURE);
  }
}
